import { useState, useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AccountShell } from "@/components/customer/AccountShell";
import { useCustomerSubscriptionList, subscriptionErrorMessage, CUSTOMER_SUBSCRIPTIONS_PAGE_SIZE } from "@/hooks/useCustomerSubscriptions";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Calendar, Package, MapPin, RefreshCcw } from "lucide-react";
import { formatISTDate } from "@/lib/date/ist";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_customer/account/subscriptions/")({
  component: SubscriptionsListPage,
  head: () => ({
    meta: [
      { title: "Your subscriptions | Poonam's Healthy Kitchen" },
      {
        name: "description",
        content: "View and manage your active meal plan subscriptions.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function SubscriptionsListPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, isFetching, dataUpdatedAt, refetch } = useCustomerSubscriptionList(page);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdated(new Date(dataUpdatedAt).toLocaleTimeString());
    }
  }, [dataUpdatedAt]);

  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / CUSTOMER_SUBSCRIPTIONS_PAGE_SIZE));
  const rows = data?.rows ?? [];

  return (
    <AccountShell 
      title="Your subscriptions" 
      description="View your active and past meal plan subscriptions."
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handleManualRefresh}
          disabled={isRefreshing || isLoading}
          className="h-8 text-xs gap-2"
        >
          <RefreshCcw className={cn("h-3 w-3", isRefreshing && "animate-spin")} />
          {isRefreshing ? "Refreshing..." : "Refresh Subscription"}
        </Button>
        {lastUpdated && !isLoading && (
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <RefreshCcw className="h-3 w-3" />
            Last updated: {lastUpdated}
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="space-y-3" aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading your subscriptions</span>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <div role="alert" className="rounded-lg border border-destructive/20 bg-destructive/5 p-6 text-sm text-destructive">
          {subscriptionErrorMessage(error)}
        </div>
      ) : rows.length === 0 ? (
        <section
          aria-labelledby="subscriptions-empty-heading"
          className="rounded-lg border border-border/60 bg-card p-8 text-center"
        >
          <h2 id="subscriptions-empty-heading" className="text-base font-semibold">
            No subscriptions yet
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
            You don't have an active subscription yet. Explore our meal plans to get started.
          </p>
          <Button asChild className="mt-5">
            <Link to="/meal-plans">Explore meal plans</Link>
          </Button>
        </section>
      ) : (
        <>
          <ul className="space-y-4">
            {rows.map((s) => (
              <li key={s.id}>
                <Link
                  to="/account/subscriptions/$subscriptionId"
                  params={{ subscriptionId: s.id }}
                  className="group block rounded-xl border border-border/60 bg-card p-5 transition-all hover:border-primary/30 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg leading-none group-hover:text-primary transition-colors">
                          {s.plan_name}
                        </h3>
                        <SubscriptionStatusBadge status={s.status} />
                      </div>
                      <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                        REF: {s.originating_order_ref || s.id.slice(0, 8)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4 text-primary/70" />
                      <span>
                        {formatISTDate(s.start_date)} — {formatISTDate(s.end_date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Package className="h-4 w-4 text-primary/70" />
                      <span>
                        {s.total_meals_allocated - s.remaining_meals} / {s.total_meals_allocated} meals delivered
                      </span>
                    </div>
                    {s.address_text && (
                      <div className="flex items-center gap-2 text-muted-foreground sm:col-span-2 md:col-span-1">
                        <MapPin className="h-4 w-4 text-primary/70 shrink-0" />
                        <span className="truncate">{s.address_text}</span>
                      </div>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-border/60 pt-6">
              <p className="text-xs text-muted-foreground" aria-live="polite">
                Page {page} of {totalPages} · {totalCount} {totalCount === 1 ? "subscription" : "subscriptions"} in total
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </AccountShell>
  );
}

function SubscriptionStatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    active: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    paused: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    cancelled: "bg-muted text-muted-foreground border-border",
    completed: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    expired: "bg-muted text-muted-foreground border-border",
  };

  return (
    <Badge 
      variant="outline" 
      className={`capitalize font-medium px-2 py-0 text-[10px] ${variants[status.toLowerCase()] || ""}`}
    >
      {status}
    </Badge>
  );
}
